import React, { useMemo } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea,
} from 'recharts';
import { BatteryMedium } from 'lucide-react';
import ChartCard from './ChartCard';
import ChartZoomHeader from './ChartZoomHeader';
import { useChartZoom } from './useChartZoom';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, LEGEND_STYLE,
} from './chartTheme';

function FullDualLine({ data, yKey, yLabel, yName, color }) {
  const zoom = useChartZoom();
  return (
    <div>
      <ChartZoomHeader caption={`${yName} vs Time`} isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId={`zoom-reset-${yKey}`} />
      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart
          data={data} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
          onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
        >
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="time_s" type="number" domain={zoom.xDomain} allowDataOverflow
            tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            label={{ value: 'Time (s)', position: 'insideBottom', offset: -4, style: AXIS_LABEL_STYLE }}
          />
          <YAxis
            yAxisId="metric" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            label={{ value: yLabel, angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }} width={64}
          />
          <YAxis
            yAxisId="speed" orientation="right" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            label={{ value: 'Speed (km/h)', angle: 90, position: 'insideRight', style: AXIS_LABEL_STYLE }} width={56}
          />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} formatter={(v) => Number(v).toFixed(2)} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          <Line yAxisId="metric" type="monotone" dataKey={yKey} name={yName} stroke={color} dot={false} strokeWidth={1.5} isAnimationActive={false} />
          <Line yAxisId="speed" type="monotone" dataKey="speed_kph" name="Vehicle Speed (km/h)" stroke={SERIES_COLORS[3]} dot={false} strokeWidth={1.25} isAnimationActive={false} />
          {zoom.isSelecting && (
            <ReferenceArea yAxisId="metric" x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Battery Discharge Graphs (§6 plots 12-14): SOC, Regeneration Power, and Discharging
 * Power over time along the selected drive cycle, each with vehicle speed overlaid on a
 * secondary axis (per product decision) — matching the Time-on-X + speed-overlay pattern
 * used by BatteryEnergyConsumptionChart, since speed is non-monotonic over a real cycle
 * and reads as an unreadable scatter when used as the X axis.
 *
 * Discharging / Regen Power are the positive / negative parts of the SAME per-step battery
 * power the SoC and cumulative-energy traces are integrated from (motor loss map, inverter,
 * driveline, auxiliary load, and internal-resistance loss — computed server-side in
 * simulation/simulate.py::simulate_drive_cycle_backward), so all three charts agree.
 */
const BatteryDischargeChart = ({ batterySeries }) => {
  const data = useMemo(() => {
    if (!batterySeries) return [];
    return batterySeries.time_s.map((t, i) => ({
      time_s: t,
      speed_kph: batterySeries.speed_kph[i],
      soc_pct: batterySeries.soc_pct[i],
      regen_power_kw: batterySeries.regen_power_kw[i],
      discharging_power_kw: batterySeries.discharging_power_kw[i],
    }));
  }, [batterySeries]);

  if (data.length === 0) return null;

  return (
    <ChartCard
      icon={BatteryMedium}
      title="Battery Discharge Graphs"
      description="SOC, regeneration power, and discharging power over time along the selected drive cycle, with vehicle speed overlaid."
      testId="chart-battery-discharge"
    >
      <div className="space-y-8">
        <FullDualLine data={data} yKey="soc_pct" yLabel="SOC (%)" yName="SOC (%)" color={SERIES_COLORS[0]} />
        <FullDualLine data={data} yKey="regen_power_kw" yLabel="Regen (kW)" yName="Regeneration Power (kW)" color={SERIES_COLORS[2]} />
        <FullDualLine data={data} yKey="discharging_power_kw" yLabel="Discharge (kW)" yName="Discharging Power (kW)" color={SERIES_COLORS[7]} />
      </div>
    </ChartCard>
  );
};

export default BatteryDischargeChart;
